import React, { ReactElement } from "react";
import {
  Button,
  ButtonContainer,
  SectionHeading,
  ContentSection,
  Paragraph,
  Fieldset,
  Form,
} from "@vwfs-bronson/bronson-react";
import { Formik, FormikHelpers as FormikActions, FastField } from "formik";
import { Output } from "../../Text";
import { FormSectionGroup2 } from "../Sections/FormSection";
import * as Validation from "../../validation";
import { CheckboxField } from "../../formik";
import { Analytics } from '../../services/analytics';
import { trackErrors, trackAbort, trackFirstCustomerInteraction, setActualPage } from '../../pages/page.utils'

const ALLOW_UNSAFE_MAIL_COMMUNICATION = "allowUnsafeMailCommunication";

class BasicAppFormPage extends React.Component<BasicAppFormPageProps, any> {
  pos: any;
  errorMessage: string = 'Consent email communication (DAC)'

  constructor(props: BasicAppFormPageProps) {
    super(props);

    this.state = {
      section: props.initialSection | 0,
      values: {
        allowUnsafeMailCommunication: undefined,
      },
      changed: false,
      lastTouchedField: undefined,
      errors: []
    };

    this.pos = {};
  }

  pageName = 'Personal details (DAC)'

  defaultTrackingData = {
    [Analytics.keys.pageName()]: this.pageName,
    [Analytics.keys.viewChange()]: this.pageName,
    [Analytics.keys.formType()]: 'Personal data (DAC)',
    [Analytics.keys.formName()]: 'Personal data (DAC)'
  }

  trackBasicAppFormData = () => {
    setTimeout(() => {
      const basicAppFormData = {
        [Analytics.keys.eventType(0)]: 'pageView',
        [Analytics.keys.eventAction(0)]: 'Success',
      }
      Analytics.setTrackingData({...this.defaultTrackingData, ...basicAppFormData})
      Analytics.trackPage()
    }, 1000)
  }

  trackSuccess = () => {
    const successData = {
      [Analytics.keys.eventType(0)]: 'interAction',
      [Analytics.keys.eventAction(0)]: 'Success',
      [Analytics.keys.linkInformation(0)]: 'Personal data (DAC) provided',
    }
    Analytics.setTrackingData({...this.defaultTrackingData, ...successData})
    Analytics.trackInteraction()
  }

  onBlur = (ev: any) => {
    if (ev && ev.target && ev.target.name) {
      this.setState({
        lastTouchedField: ev.target.name.split('.')[ev.target.name.split('.').length - 1]
      })
      if (!this.state.changed) {
        this.setState({changed: true})
        trackFirstCustomerInteraction(this.defaultTrackingData)
      }
    }
  }

  onUnload = () => {
    trackAbort(this.state.lastTouchedField, this.defaultTrackingData)
  }

  ownUnload = () => {
    window.addEventListener('unload', this.onUnload)
  }

  desubscribeUnload = () => {
    window.removeEventListener('unload', this.onUnload)
  }

  componentDidMount() {
    setActualPage(this.pageName)
    window.scrollTo(0, 0);
    this.trackBasicAppFormData()
    this.ownUnload()
  }

  componentWillUnmount() {
    this.desubscribeUnload()
  }

  next = (values: any) =>
    this.setState((state: any) => ({
      section: state.section + 1,
      values,
    }));

  previous = () =>
    this.setState((state: any) => ({
      section: Math.max(state.section - 1, 0),
    }));

  getSectionYOffset = (section: number) => {
    const pos = this.pos[section];
    return pos ? pos.getBoundingClientRect().y + window.pageYOffset : 0;
  };

  scrollSectionIntoView = (section: number) => {
    window.scrollTo(0, this.getSectionYOffset(section));
  };

  handleSubmit = (values: any, bag: any) => {
    const { section } = this.state;
    const { onSubmit } = this.props;

    if (section === 1) {
      return onSubmit({ ...this.state.values, ...values }, bag, this.trackSuccess);
    }

    this.next(values);
    bag.setTouched({});

    this.scrollSectionIntoView(section);
  };

  getNextSectionName = (index: number) => {
    return ["Personal Data"][index] || "";
  };

  getDataProtectionDocUrl() {
    const brand = process.env.REACT_APP_BRAND;
    switch (brand) {
      case "vwn":
        return `\\assets\\vw\\dsi\\vw_leasing_dsi_data_protection.pdf`;
      default:
        return `\\assets\\${brand}\\dsi\\${brand}_leasing_dsi_data_protection.pdf`;
    }
  }

  render() {
    const { children } = this.props;
    const { section } = this.state;

    return (
      <ContentSection pageWrap pageWrapSize="medium">
        <div className="center-header">
        <SectionHeading
          center
          primary
          level="1"
          smallSpacing={true}
          subtitle={Output.resolve("pages.leasing.basicAppForm.subtitle")}
        >
          {Output.resolve("pages.leasing.basicAppForm.title")}
        </SectionHeading>
        </div>
         <ContentSection.ComponentWrapper>
          <FormSectionGroup2>
            <Formik
              initialValues={this.props.initialValues}
              validate={(values) => {
                const component = React.Children.toArray(children)[
                  section
                ] as ReactElement;

                const validationResult =
                  component && component.props.validate
                    ? component.props.validate(values)
                    : {};

                if (Object.keys(validationResult).length !== 0) {
                  this.scrollSectionIntoView(section);
                }

                return validationResult;
              }}
              enableReinitialize={false}
              onSubmit={this.handleSubmit}
              render={({ values, handleSubmit, errors }) => {
                const handleSubmitOwn = (e: any) => {
                  trackErrors(errors, this.defaultTrackingData)
                  handleSubmit(e)
                }
                return (
                <>
                  {React.Children.map(children, (child, index) => {
                    const onSectionSubmit = () => {
                      const childValidation = child.props.validate(values);
                      if (Object.keys(childValidation).length !== 0) {
                        return;
                      }

                      if (this.props.onSectionSubmit) {
                        this.props.onSectionSubmit({ values });
                      }

                      const onChildSectionSubmit = child.props.onSectionSubmit;

                      if (onChildSectionSubmit) {
                        onChildSectionSubmit(values)
                      }
                    };

                    return React.cloneElement(child, {
                      containerRef: (e: ReactElement) => {
                        this.pos[index] = e;
                      },
                      title: child.props.title,
                      subtitle: child.props.subtitle,
                      form: { onSubmit: handleSubmitOwn, onBlur: this.onBlur },
                      closed: section !== index,
                      success: index < section,
                      showEditButton: index < section,
                      onIconClick: () => {
                        if (index < section) {
                          this.setState({
                            section: index,
                          });
                        }
                      },
                      buttons: (
                        <ButtonContainer right>
                          <Button
                            className={"appFixedButton"}
                            type={"submit"}
                            small
                            onClick={onSectionSubmit}
                            testId={`${this.getNextSectionName(index)}-CTA`}
                          >
                            {Output.resolve("pages.applicationForm.buttonNext")}
                          </Button>
                        </ButtonContainer>
                      ),
                    });
                  })}
                </>
              )
              }}
            />
          </FormSectionGroup2>
        </ContentSection.ComponentWrapper>
        <hr />
         <ContentSection.ComponentWrapper>
          <SectionHeading level={"3"} smallSpacing={true}>
            {Output.resolve("pages.leasing.basicAppForm.dsi.title")}
          </SectionHeading>
          <Paragraph>
            {Output.resolve("pages.leasing.basicAppForm.dsi.dsiLinkPrefix")}{" "}
            <a
              href={this.getDataProtectionDocUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {Output.resolve("pages.leasing.basicAppForm.dsi.dsiLinkText")}
            </a>
          </Paragraph>
        </ContentSection.ComponentWrapper>

         <ContentSection.ComponentWrapper>
          <Formik
            initialValues={this.state.values}
            isInitialValid={false}
            onSubmit={this.handleSubmit}
            validate={(values) => {
              if (this.state.section !== 1) {
                this.scrollSectionIntoView(this.state.section);
                return { sectionsValid: "false" };
              }
              if (this.props.tlsCheckResult === true) {
                return;
              }

              const value = {
                allowUnsafeMailCommunication: values.allowUnsafeMailCommunication
                  ? "true"
                  : values.allowUnsafeMailCommunication,
              };

              return Validation.select(ALLOW_UNSAFE_MAIL_COMMUNICATION)(value);
            }}
            render={({ values, handleSubmit }) => (
              <Form onSubmit={handleSubmit} onBlur={this.onBlur} floatingLabel>
                {this.props.tlsCheckResult === false && (
                  <>
                    <SectionHeading
                      level="5"
                      subtitle={Output.resolve(
                        "pages.leasing.basicAppForm.tls.subtitle"
                      )}
                    >
                      {Output.resolve("pages.leasing.basicAppForm.tls.title")}
                    </SectionHeading>
                    <Fieldset>
                      <Fieldset.Row>
                        <FastField
                          name={ALLOW_UNSAFE_MAIL_COMMUNICATION}
                          label={Output.resolve(
                            "pages.leasing.basicAppForm.tls.checkBoxText"
                          )}
                          component={CheckboxField}
                        />
                      </Fieldset.Row>
                    </Fieldset>
                    <Paragraph>
                      <b>
                        {Output.resolve(
                          "pages.leasing.basicAppForm.tls.revocationContent"
                        )}
                      </b>
                    </Paragraph>
                  </>
                )}
                <hr />
                <div className="o-button-container o-button-container--fixed o-button-container--right">
                  <Button
                    disabled={
                      this.props.isLoading ||
                      (!values.allowUnsafeMailCommunication &&
                        this.props.tlsCheckResult === false)
                    }
                    type={"submit"}
                    small
                    testId="basicAppForm-global-CTA"
                  >
                    {Output.resolve("pages.applicationForm.buttonNext")}
                  </Button>
                </div>
              </Form>
            )}
          />
        </ContentSection.ComponentWrapper>
      </ContentSection>
    );
  }
}

type BasicAppFormPageProps = {
  initialValues: Object;
  initialSection: number;
  onSectionSubmit: Function;
  onSubmit: (values: any, formikActions: FormikActions<any>, callback: any) => void;
  children: ReactElement[];
  tlsCheckResult: boolean;
  isLoading: boolean;
};

export { BasicAppFormPage };
